/**
 * Which qv-data this build talks to — and the one place that decides.
 *
 * Ten modules used to read `import.meta.env.VITE_QV_*` for themselves. That is
 * fine while the answer is fixed at build time, and it is exactly what stopped
 * the answer from being changeable: `import.meta.env` is substituted by Vite
 * during the build, so pointing the app at a deployed backend meant editing
 * `.env` and restarting the dev server.
 *
 * This resolves the same values in three tiers, the first of which is writable
 * at runtime:
 *
 *   1. `localStorage["qv.backend"]` — an admin override, set from the app menu.
 *   2. `import.meta.env.VITE_QV_*` — the build-time value, unchanged.
 *   3. `""` — relative, which is Vite's dev proxy in development and the same
 *      origin in a deployed build.
 *
 * **Read once at module load**, which is the rule every one of those modules
 * already followed. Changing the override therefore needs a reload, and the
 * menu does one — the same reason and the same idiom as `ActingAs`: every open
 * surface loaded its data on mount, and all of it now belongs to a different
 * backend.
 *
 * ## The WebSocket URLs are derived, not stored
 *
 * `VITE_QV_WS_URL` and `VITE_QV_SCR_URL` are full socket URLs where the others
 * are HTTP bases, so an override that only knew about HTTP would leave the
 * chart and the screener pointed at the old backend — the failure being two
 * halves of one screen disagreeing about which market data they show.
 *
 * Deriving them also gets the scheme right, which is the part that bites:
 * `https://qf-us.fly.dev` must become `wss://`, and a hand-typed `ws://` against
 * an HTTPS origin is refused by the browser as mixed content with an error that
 * names neither the scheme nor the setting.
 */

/** Where an admin override is kept. Same store as `qv.actingAs`. */
export const BACKEND_KEY = "qv.backend";

/** The deployed US backend, offered by name so nobody has to remember it. */
export const QF_US = "https://qf-us.fly.dev";

function stored(): string {
  // Wrapped because a private window, cleared site data, or a browser set to
  // block storage all throw rather than return null — and a backend selector
  // that crashes the app it configures is worse than one that ignores you.
  try {
    return (localStorage.getItem(BACKEND_KEY) ?? "").trim();
  } catch {
    return "";
  }
}

function trimSlash(value: string): string {
  return value.replace(/\/$/, "");
}

/**
 * A URL escape hatch, read **before** anything else and applied immediately.
 *
 *   ?backend=reset                 clear the override
 *   ?backend=https://qf-us.fly.dev set one
 *
 * This exists because the menu is not a safe place to keep the only way out.
 * The control that changes the backend lives *inside the app the backend
 * serves*, so a backend that hangs — one with no data, one still seeding, one
 * that is simply down — can stop the menu rendering and strand the setting that
 * would undo it. That happened the first time it was used.
 *
 * A query parameter needs no working backend, no menu and no console, and it
 * survives a page that never finishes loading.
 */
function escapeHatch(): string | null {
  try {
    const asked = new URLSearchParams(window.location.search).get("backend");
    if (asked === null) return null;
    const value = asked === "reset" ? "" : trimSlash(asked.trim());
    setBackend(value);
    // Strip it so a refresh, a bookmark or a shared link does not silently
    // reapply a backend the user has since changed in the menu.
    const url = new URL(window.location.href);
    url.searchParams.delete("backend");
    window.history.replaceState({}, "", url);
    return value;
  } catch {
    return null;
  }
}

const OVERRIDE = escapeHatch() ?? trimSlash(stored());

/**
 * The HTTP base for `/set/*`, `/sym/*`, `/ind/*`, `/journal/*` and `/auth/*`.
 *
 * All of them are one service on one origin, which is why `VITE_QV_SYM_URL` and
 * `VITE_QV_IND_URL` already fall back to `VITE_QV_SET_URL` — nobody should have
 * to set three variables to the same value, and an override sets none of them.
 */
export function httpBase(...preferred: (string | undefined)[]): string {
  if (OVERRIDE) return OVERRIDE;
  for (const value of preferred) {
    if (value) return trimSlash(value);
  }
  return "";
}

/**
 * A WebSocket URL under the current backend, or `fallback` when there is no
 * override.
 *
 * `fallback` is the build-time variable, kept whole: it may point somewhere
 * unrelated to the HTTP base and an override is the only thing entitled to
 * replace it.
 */
export function wsUrl(path: string, fallback: string | undefined): string {
  if (!OVERRIDE) return fallback ?? "";
  // `https:` -> `wss:`, `http:` -> `ws:`. Anything else is a typed URL we do not
  // recognise, and guessing a scheme for it would produce a socket that fails to
  // open for a reason the user cannot see.
  const scheme = OVERRIDE.startsWith("https:")
    ? "wss:"
    : OVERRIDE.startsWith("http:")
      ? "ws:"
      : "";
  if (!scheme) return fallback ?? "";
  return scheme + OVERRIDE.slice(OVERRIDE.indexOf("//")) + path;
}

/** What the menu shows as current. Empty means "this origin / the dev proxy". */
export function currentBackend(): string {
  return OVERRIDE;
}

/**
 * Point the app at `url`, or clear the override with `""`.
 *
 * Does **not** reload — the caller does, so the menu can close first and so a
 * test can set it without navigating.
 */
export function setBackend(url: string): void {
  try {
    const value = trimSlash(url.trim());
    if (value) localStorage.setItem(BACKEND_KEY, value);
    else localStorage.removeItem(BACKEND_KEY);
  } catch {
    // Storage unavailable: the setting simply does not persist. Better than
    // throwing out of a menu handler.
  }
}
